<template>
  <div
    class="bg-action-default-mid text-action-default-hi rounded-full flex w-full flex-row-reverse items-center gap-4 pl-4 pr-8 focus-within:!bg-action-default-mid hover:bg-action-default-mid-hover"
  >
    <button
      v-show="inputValue"
      :aria-label="i18n(translations.resetSearchButtonAriaLabel)"
      class="text-action-default-hi focus-visible-outline-inset-hi rounded-sm h-32 w-32 cursor-pointer items-center justify-center transition-colors duration-100 hover:text-action-default-hi-hover"
      type="reset"
      @click="resetHandler"
    >
      ✕
    </button>
    <div class="relative w-full">
      <label v-if="hasAnimatedPlaceholder" class="sr-only" :for="inputId">
        {{ i18n(translations.textboxPlaceholder) }}
      </label>
      <input
        :id="inputId"
        ref="input"
        v-model="inputValue"
        aria-describedby="resultsListBoxAlert"
        :aria-expanded="isListboxOpen"
        autocomplete="off"
        autocorrect="off"
        class="text-action-default-hi peer body-1 h-[40px] w-full appearance-none text-ellipsis bg-transparent outline-none placeholder:text-action-default-low focus:placeholder:text-transparent"
        data-qa="search-bar-input"
        :name="inputName"
        :placeholder="
          hasAnimatedPlaceholder ? '' : i18n(translations.textboxPlaceholder)
        "
        role="searchbox"
        type="text"
        @blur="isFocused = false"
        @focus="isFocused = true"
        @keydown.down.prevent="specialKeyHandler"
        @keydown.enter.prevent="submitHandler"
        @keydown.esc.prevent="resetHandler"
        @keydown.up.prevent="specialKeyHandler"
      />
      <TextboxAnimatedPlaceholder
        v-if="hasAnimatedPlaceholder && !isFocused && !inputValue"
        class="absolute left-4 right-4 top-1/2 -translate-y-1/2"
        :translations="[
          i18n(translations.placeholder1),
          i18n(translations.placeholder2),
          i18n(translations.placeholder3),
          i18n(translations.placeholder4),
          i18n(translations.placeholder5),
        ]"
      />
    </div>
    <button
      :aria-label="i18n(translations.searchButtonAriaLabel)"
      class="text-action-default-hi focus-visible-outline-inset-hi rounded-sm flex h-32 w-32 shrink-0 cursor-pointer items-center justify-center transition-colors duration-100 hover:text-action-default-hi-hover"
      type="submit"
      @click="submitHandler"
    >
      <IconSearch size="24" />
    </button>
    <div id="resultsListBoxAlert" class="sr-only">
      <p role="alert">{{ listboxResultsMessage }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, useTemplateRef } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { IconSearch } from '@ds/icons/IconSearch'

import { DEFAULT_INPUT_NAME } from './TextBox.constants'
import translations from './Textbox.translations'
import TextboxAnimatedPlaceholder from './TextboxAnimatedPlaceholder.vue'

const i18n = useI18n()

const emit = defineEmits(['submit', 'keydown'])

withDefaults(
  defineProps<{
    inputName?: string
    inputId: string
    isListboxOpen: boolean
    listboxResultsMessage: string
  }>(),
  {
    inputName: DEFAULT_INPUT_NAME,
  },
)

defineExpose({
  focusInput,
})

const inputValue = defineModel<string | null>()
const inputRef = useTemplateRef('input')
const experiments = useExperiments()
const isFocused = ref(false)

const hasAnimatedPlaceholder =
  experiments['experiment.dynamicSearchBarPlaceholder'] === 'with'

function submitHandler() {
  emit('submit')
  blurInput()
}

function specialKeyHandler({ code }: { code: string }) {
  if (code === 'ArrowUp') {
    emit('keydown', -1)
  } else if (code === 'ArrowDown') {
    emit('keydown', 1)
  }
}

function focusInput() {
  inputRef.value?.focus()
  const valueLength = inputValue.value?.length ?? 0
  inputRef.value?.setSelectionRange(valueLength, valueLength)
}

function blurInput() {
  inputRef.value?.blur()
}

function resetHandler() {
  inputValue.value = ''
  focusInput()
}

onMounted(() => {
  if (inputRef.value === document.activeElement) {
    focusInput()
  }
})
</script>
